/**
 * End User Response Page
 *
 * Page for end users to respond to advisory invitations
 * Flow:
 * 1. Validate invitation link
 * 2. Show form if link is valid
 * 3. Submit response and update link status
 * 4. Show success confirmation
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Button from 'components/Button';
import Text from 'components/Text';
import Spin from 'components/Spin';
import ResponseForm from './components/ResponseForm';
import SuccessPage from './components/SuccessPage';
import { useInvitationLink } from './hooks/useInvitationLink';
import { useFetchCompleteForm } from 'hooks/useFetchCompleteForm';
import { useUser } from 'global/UserContext';
import { InvitationLinkStatus } from 'types/leadRequestsTypes';
import * as S from './styles';
import * as CS from './components/styles';

enum Step {
  VALIDATING = 1,
  FORM = 2,
  SUBMITTING = 3,
  SUCCESS = 4,
}

export default function EndUserResponse() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>(Step.VALIDATING);
  const { user } = useUser();

  const { linkData, requestData, validating, submitting, error, submitResponse } =
    useInvitationLink(linkId);

  // Fetch form data to check if user has filled it
  const { formData, loading: loadingForm } = useFetchCompleteForm(
    'enduser-advisory-form',
    user?.id || ''
  );

  // Handle validation result
  useEffect(() => {
    if (!validating && linkData) {
      if (
        linkData.status === InvitationLinkStatus.NEW ||
        linkData.status === InvitationLinkStatus.PENDING
      ) {
        setCurrentStep(Step.FORM);
      }
    }
  }, [validating, linkData]);

  const handleFormSubmit = useCallback(async () => {
    if (!formData || formData.length === 0) {
      return;
    }

    // Convert form fields with submissions to JSON format
    const form = formData[0];
    const formResponses: Record<string, any> = {};

    form.fields.forEach(field => {
      if (field.submission && field.submission.values) {
        const values = field.submission.values.map(v => v.value);
        formResponses[field.label] = values.length === 1 ? values[0] : values;
      }
    });

    // Submit the response
    setCurrentStep(Step.SUBMITTING);
    const success = await submitResponse(formResponses);

    if (success) {
      setCurrentStep(Step.SUCCESS);
    } else {
      setCurrentStep(Step.FORM);
    }
  }, [formData, submitResponse]);

  // Show error state for expired/used/invalid links
  if (error && !validating) {
    return (
      <S.Container>
        <CS.ErrorContainer>
          <CS.ErrorIcon>
            {error.includes('expired')
              ? '⏰'
              : error.includes('used')
                ? '✓'
                : '❌'}
          </CS.ErrorIcon>

          <Text size="XXLarge" bold textAlign="center" color="red">
            {error.includes('expired')
              ? 'Link Expirado'
              : error.includes('used')
                ? 'Link Já Utilizado'
                : 'Link Inválido'}
          </Text>

          <Box mt={2} mb={3}>
            <Text size="medium" textAlign="center" color="textSecondary">
              {error}
            </Text>
          </Box>

          <Box mt={2}>
            <Text size="medium" textAlign="center" color="textSecondary">
              Por favor, entre em contato com a pessoa que lhe enviou este link para obter um novo convite.
            </Text>
          </Box>

          <Box mt={4}>
            <Button variant="primary" onClick={() => navigate('/cockpit')} size="medium">
              Ir para o Painel
            </Button>
          </Box>
        </CS.ErrorContainer>
      </S.Container>
    );
  }

  // Show loading state during validation or form loading
  if (currentStep === Step.VALIDATING || loadingForm) {
    return (
      <S.Container>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <Spin />
          <Box mt={2}>
            <Text size="medium" textAlign="center">
              {currentStep === Step.VALIDATING ? 'Validando convite...' : 'Carregando formulário...'}
            </Text>
          </Box>
        </Box>
      </S.Container>
    );
  }

  // Show success page
  if (currentStep === Step.SUCCESS) {
    return (
      <S.Container>
        <SuccessPage />
      </S.Container>
    );
  }

  // Show form
  return (
    <S.Container>
      <S.Header>
        <S.Title>Resposta da Reunião de Consultoria</S.Title>
        <Text size="medium" color="textSecondary">
          Você foi pareado com um patrocinador. Por favor, preencha o formulário abaixo para nos ajudar a preparar a melhor reunião para você.
        </Text>
      </S.Header>

      {requestData && (
        <S.InfoSection>
          <Text size="small" bold color="textPrimary">
            Sobre esta solicitação:
          </Text>
          <Text size="small" color="textSecondary">
            ID da Solicitação: {requestData.id}
          </Text>
        </S.InfoSection>
      )}

      <S.ContentSection>
        <ResponseForm />

        <S.ActionButtons>
          <Button variant="outline" onClick={() => navigate('/cockpit')} size="medium">
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            loading={submitting || currentStep === Step.SUBMITTING}
            disabled={submitting || currentStep === Step.SUBMITTING}
            size="medium"
          >
            Enviar Resposta
          </Button>
        </S.ActionButtons>
      </S.ContentSection>
    </S.Container>
  );
}
